import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { EditorState, LexicalEditor } from 'lexical';

import EditorToolbar from './EditorToolbar';
import { PageBreakNode } from './nodes/PageBreakNode';
import MarginControls from './MarginControls';
import { MarginSettings } from './MarginContext';
import './LexicalResumeEditor.css';

// Horizontal Ruler Component (in inches)
const HorizontalRuler: React.FC = () => {
  const marks = useMemo(() => {
    const items = [];
    // 8.5 inches with 0.5 inch major marks
    for (let i = 0; i <= 85; i++) {
      const isMajor = i % 10 === 0;
      const isHalf = i % 5 === 0 && !isMajor;
      items.push(
        <div
          key={i}
          className={`ruler-mark ${isMajor ? 'ruler-mark--major' : ''} ${isHalf ? 'ruler-mark--half' : ''}`}
          style={{ left: `${(i / 85) * 100}%` }}
        >
          {isMajor && <span className="ruler-number">{i / 10}</span>}
        </div>
      );
    }
    return items;
  }, []);

  return (
    <div className="horizontal-ruler">
      <div className="ruler-marks">{marks}</div>
    </div>
  );
};

interface LexicalResumeEditorProps {
  initialContent?: string;
  onChange?: (content: string, editor: LexicalEditor) => void;
  editorRef?: React.MutableRefObject<LexicalEditor | null>;
  margins?: MarginSettings;
}

const theme = {
  ltr: 'ltr',
  rtl: 'rtl',
  paragraph: 'editor-paragraph',
  quote: 'editor-quote',
  heading: {
    h1: 'editor-heading-h1',
    h2: 'editor-heading-h2',
    h3: 'editor-heading-h3',
  },
  list: {
    nested: {
      listitem: 'editor-nested-listitem',
    },
    ol: 'editor-list-ol',
    ul: 'editor-list-ul',
    listitem: 'editor-listitem',
  },
  link: 'editor-link',
  text: {
    bold: 'editor-text-bold',
    italic: 'editor-text-italic',
    underline: 'editor-text-underline',
    strikethrough: 'editor-text-strikethrough',
    code: 'editor-text-code',
  },
};

function onError(error: Error): void {
  console.error('Lexical Error:', error);
}

const LexicalResumeEditor: React.FC<LexicalResumeEditorProps> = ({
  initialContent,
  onChange,
  editorRef,
  margins,
}) => {
  const innerRef = useRef<HTMLDivElement>(null);
  
  const marginStyle = useMemo(() => {
    if (!margins) return {};
    return {
      '--margin-top': `${margins.top}in`,
      '--margin-bottom': `${margins.bottom}in`,
      '--margin-left': `${margins.left}in`,
      '--margin-right': `${margins.right}in`,
    } as React.CSSProperties;
  }, [margins]);

  const initialConfig = {
    namespace: 'ResumeEditor',
    theme,
    onError,
    nodes: [ListNode, ListItemNode, LinkNode, HeadingNode, QuoteNode, PageBreakNode],
    editorState: initialContent ? initialContent : undefined,
  };

  // Scroll to top when content is loaded
  useEffect(() => {
    if (initialContent && innerRef.current) {
      // Reset scroll after a small delay to ensure content is rendered
      const timer = setTimeout(() => {
        if (innerRef.current) {
          innerRef.current.scrollTop = 0;
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [initialContent]);

  const handleChange = useCallback(
    (editorState: EditorState, editor: LexicalEditor) => {
      if (editorRef) {
        editorRef.current = editor;
      }
      if (onChange) {
        editorState.read(() => {
          const json = JSON.stringify(editorState.toJSON());
          onChange(json, editor);
        });
      }
    },
    [onChange, editorRef]
  );

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="lexical-editor-container">
        <EditorToolbar />
        <MarginControls />
        <div className="lexical-editor-workspace">
          {/* Horizontal Ruler */}
          <div className="ruler-container">
            <div className="ruler-corner" />
            <HorizontalRuler />
          </div>
          {/* Document Area */}
          <div className="lexical-editor-inner" ref={innerRef}>
            <div className="lexical-editor-page" style={marginStyle}>
              <RichTextPlugin
                contentEditable={
                  <ContentEditable className="lexical-editor-input" />
                }
                placeholder={
                  <div className="lexical-editor-placeholder">
                    Start typing your resume content...
                  </div>
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
            </div>
            <HistoryPlugin />
            <ListPlugin />
            <LinkPlugin />
            <OnChangePlugin onChange={handleChange} />
          </div>
        </div>
      </div>
    </LexicalComposer>
  );
};

export default LexicalResumeEditor;
